// Workflow Navigation - Back/Next buttons with cell count feedback

import { h } from 'preact'
import { useLocation } from 'wouter-preact'
import { currentStep, nextStep, previousStep, resetWorkflow } from '../../state/benchmarkingWorkflowState.js'
import { compareCells } from '../../state/filterStore.js'
import { IconArrowLeft, IconArrowRight, IconCheck, IconRefresh } from '../shared/Icons.jsx'

export default function WorkflowNavigation({ cellCounts }) {
  const [location, setLocation] = useLocation()
  
  const stepRoutes = {
    1: 'fundamentals',
    2: 'performance',
    3: 'cells'
  }
  
  const handleNext = () => {
    if (currentStep.value === 3) {
      // Last step: navigate to compare if cells are selected
      if (compareCells.value?.length >= 2) {
        setLocation('/compare')
      }
      return
    }
    if (currentStep.value < 3) {
      nextStep()
      setLocation(`/cell-selection?step=${stepRoutes[currentStep.value]}`)
    }
  }
  
  const handlePrevious = () => {
    if (currentStep.value > 1) {
      previousStep()
      setLocation(`/cell-selection?step=${stepRoutes[currentStep.value]}`)
    }
  }
  
  // Build step-by-step cell count chain: Total → Step1 → Step2 → Step3
  const stepCounts = [
    { label: 'Total', count: cellCounts.total },
    { label: 'Fundamentals', count: cellCounts.fundamentals },
    { label: 'Performance', count: cellCounts.performance },
    { label: 'Matching Cells', count: cellCounts.cells }
  ]
  
  const isLastStep = currentStep.value === 3
  const isFirstStep = currentStep.value === 1
  
  return (
    <div className="workflow-navigation" data-tour="benchmarking-navigation">
      {/* Left: Back Button */}
      <button 
        className="workflow-nav-btn workflow-nav-btn--back"
        onClick={handlePrevious}
        disabled={isFirstStep}
      >
        <IconArrowLeft size={20} />
        <span>Back</span>
      </button>
      
      {/* Center: Cell Count Chain */}
      <div className="workflow-cell-count">
        <div className="cell-count-chain">
          {stepCounts.map((step, index) => {
            const isActive = index === currentStep.value
            const isPassed = index < currentStep.value
            const isFuture = index > currentStep.value
            
            return (
              <>
                {index > 0 && (
                  <IconArrowRight 
                    size={14} 
                    className={`count-chain-arrow ${isPassed ? 'passed' : isFuture ? 'future' : ''}`} 
                  />
                )}
                <div 
                  key={index}
                  className={`count-chain-step ${isActive ? 'active' : ''} ${isPassed ? 'passed' : ''} ${isFuture ? 'future' : ''}`}
                  title={step.label}
                >
                  <span className="count-chain-number">{step.count}</span>
                  {index === 0 && <span className="count-chain-label">Total</span>}
                </div>
              </>
            )
          })}
        </div>
      </div>
      
      {/* Right: Next Button + Start Over */}
      <div className="workflow-nav-right">
        <button
          className="workflow-nav-btn workflow-nav-btn--next"
          onClick={handleNext}
          disabled={isLastStep ? (compareCells.value?.length || 0) < 2 : stepCounts[currentStep.value].count === 0}
        >
          <span>{isLastStep ? `Compare ${compareCells.value?.length || 0} Cells` : 'Next'}</span>
          <IconArrowRight size={20} />
        </button>
        
        <button 
          className="workflow-reset-btn"
          onClick={() => {
            if (confirm('Start over? This will reset all selections.')) {
              resetWorkflow()
              setLocation('/cell-selection?step=fundamentals')
            }
          }}
          title="Start Over"
        >
          <IconRefresh size={18} />
          <span>Start Over</span>
        </button>
      </div>
    </div>
  )
}
